import { Link } from 'react-router-dom'

import { formatInteger, formatPercent } from './format'

export interface FunnelBarStage {
  key: string
  label: string
  count: number
  /** False when no event for the stage arrived: "Not measured" rather than a 0 and a 0 % step. Absent means measured. */
  measured?: boolean
  href?: string
}

/**
 * A conversion funnel as a table with inline bars: each stage's count, its
 * conversion from the stage before, its share of the first stage, and how many
 * were lost on the way in. The bar is decoration; the numbers are the content.
 */
export function FunnelBars({ caption, stages }: { caption: string; stages: readonly FunnelBarStage[] }) {
  const top = stages[0]?.count ?? 0
  return (
    <div className="ci-table-wrap ci-funnel" role="region" aria-label={caption} tabIndex={0}>
      <table className="ci-table ci-funnel__table">
        <caption className="ci-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Stage</th>
            <th scope="col" className="ci-funnel__bar-col">
              <span className="ci-visually-hidden">Share of first stage (bar)</span>
            </th>
            <th scope="col" data-align="end">
              Count
            </th>
            <th scope="col" data-align="end">
              Step
            </th>
            <th scope="col" data-align="end" className="ci-funnel__optional">
              Overall
            </th>
            <th scope="col" data-align="end" className="ci-funnel__optional">
              Drop-off
            </th>
          </tr>
        </thead>
        <tbody>
          {stages.map((stage, index) => {
            const previous = index > 0 ? stages[index - 1] : undefined
            const measured = stage.measured !== false
            // A step, or a loss, from an unmeasured stage (or to one) would compare against a count nobody measured.
            const comparable = measured && previous !== undefined && previous.measured !== false
            const step = comparable ? (previous.count > 0 ? stage.count / previous.count : 0) : null
            const overall = top > 0 ? stage.count / top : 0
            return (
              <tr key={stage.key} data-stage={stage.key}>
                <th scope="row">
                  {stage.href ? (
                    <Link to={stage.href} className="ci-table__link">
                      {stage.label}
                    </Link>
                  ) : (
                    stage.label
                  )}
                </th>
                <td className="ci-funnel__bar-col" aria-hidden="true">
                  <span className="ci-funnel__track">
                    {measured ? <span className="ci-funnel__bar" style={{ width: `${Math.max(1.5, overall * 100)}%` }} /> : null}
                  </span>
                </td>
                <td data-align="end">{measured ? formatInteger(stage.count) : <span className="ci-funnel__unmeasured">Not measured</span>}</td>
                <td data-align="end">{step === null ? '—' : formatPercent(step)}</td>
                <td data-align="end" className="ci-funnel__optional">
                  {measured ? formatPercent(overall) : '—'}
                </td>
                <td data-align="end" className="ci-funnel__optional">
                  {comparable ? formatInteger(previous.count - stage.count) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
